import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  selectGiaApiKeys,
  selectLanguage,
  selectStripeAccountId,
  selectStripeAccountName,
} from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { useBeaconFlushOnPageHide } from 'hooks/useBeaconFlushOnPageHide'

import { getCurrentEnv } from 'helpers/getCurrentEnv'
import { getEnvironment } from 'helpers/getEnvironment'
import { getQueryParamsFromSearchString } from 'helpers/getQueryParamsFromSearchString'

import { TGiaApiKeys } from 'models/config.model'

import { Amplitude } from 'services/amplitude.service'
import { eventLogger } from 'services/eventLogger.service'
import { retenoLogger } from 'services/retenoLogger.service'
import { UserFlowTracker } from 'services/userFlowTracker.service'

import { CurrentEnvironment, EventLoggerInstanceName } from 'root-constants'

function getGIAAmplitudeOptions() {
  const btoaAuthCredentials = 'cHJveHktdXNlcjozQyk9Q3YncTd5L0A4Pjcp'

  return {
    apiEndpoint:
      getCurrentEnv() === CurrentEnvironment.PROD
        ? 'analytics.gismart.xyz/events'
        : 'analytics.gidev.xyz/events',
    headers: {
      Authorization: `Bearer ${btoaAuthCredentials}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    forceHttps: true,
  }
}

export function useEventLoggerInitialization(cohort = ''): void {
  const { search } = useLocation()
  const uuid = useSelector(selectUUID)
  const language = useSelector(selectLanguage)
  const giaApiKeys = useSelector(selectGiaApiKeys) as TGiaApiKeys
  const stripeAccountId = useSelector(selectStripeAccountId)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const [giaInstance, setGiaInstance] = useState<Amplitude | null>(null)

  const { isProdEnvironment, isTestEnvironment } = getEnvironment()

  const utmTagsFromUrl = getQueryParamsFromSearchString(search)

  useBeaconFlushOnPageHide(giaInstance)

  useEffect(() => {
    if (uuid && giaApiKeys) {
      const giaService = new Amplitude({
        apiKey: giaApiKeys?.[language],
        instanceName: EventLoggerInstanceName.GIA,
        options: getGIAAmplitudeOptions(),
        userId: uuid,
        utmTags: utmTagsFromUrl,
        isTestEnvironment,
        isDevEnvironment: !isProdEnvironment,
      })

      const userFlowTracker = new UserFlowTracker({
        instanceName: EventLoggerInstanceName.USER_FLOW_TRACKER,
        userId: uuid,
      })

      giaService.configure({ cohort })
      userFlowTracker.configure({ cohort })

      retenoLogger.init(uuid)
      eventLogger.init(giaService, userFlowTracker)
      setGiaInstance(giaService)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, giaApiKeys])

  useEffect(() => {
    if (!stripeAccountId || !stripeAccountName) return

    giaInstance?.updateUserProperties(stripeAccountId, stripeAccountName)
  }, [giaInstance, stripeAccountId, stripeAccountName])
}
